import { ExceptionDimensions, ResponseCode, logAnalyticsEvent } from '@vestico/utils';

import {
  ClientReviewRequestResult,
  ContentReviewRequest,
  DocumentID,
  FetchBoundaries,
  ProductVariantUID,
  ProductWidget,
  ReviewRequest,
  ServerReviewRequestResult,
  SizeRecDataFineTuneRequest,
  SizeRecommendationRequest,
  SizeRecommendationRequestResult,
  SupportedMatchInfoHashCategory,
  TranslateReviewRequest,
  TranslateReviewResponse,
  UserModel,
  createClientResponse,
  createReviewRequestUserInfo,
} from '@vestico/model';

import { AuthorizedRequestInfo, ContentAPIRequest, MatchAPIRequest, MatchingApi, ProductCollectionConfiguration } from '../model/api';
import { AbstractApiClient } from './apiClient';
import { createAPIRequestURL, fetchRequest, getClientDetails } from './helpers';

interface ReviewResponseRequestProps {
  providerDocumentID: DocumentID;
  entryID?: DocumentID;
  product: ProductWidget | undefined;
  selectedVariantUID: ProductVariantUID | undefined;
  productCollection: ProductCollectionConfiguration | undefined;
  user: UserModel;
  authRequestInfo: AuthorizedRequestInfo;
  fetchURL: URL;
  selectedPost: string | undefined;
  existingPostsToKeep: string[] | undefined;
  reloadAll: boolean;
  referenceFetchBoundaries: FetchBoundaries | undefined;
  requiresProduct: boolean;
  category?: SupportedMatchInfoHashCategory;
}

export class MatchingApiClient extends AbstractApiClient implements MatchingApi {
  public async fetchMatches({ product, selectedVariantUID, user, authRequestInfo }: MatchAPIRequest): Promise<ClientReviewRequestResult | null> {
    const fetchURL = await createAPIRequestURL(authRequestInfo, '/matches');

    if (!fetchURL) {
      console.error('Carousel: cannot create fetch URL');
      logAnalyticsEvent({
        category: 'exception',
        event: `Carousel: cannot create fetch URL`,
      });
      return Promise.resolve(null);
    }

    return this.fetchReviewResponses({
      providerDocumentID: product.providerDocumentID,
      product,
      selectedVariantUID,
      user,
      authRequestInfo,
      fetchURL,
      selectedPost: undefined,
      existingPostsToKeep: undefined,
      reloadAll: false,
      referenceFetchBoundaries: undefined,
      requiresProduct: true,
      productCollection: undefined,
    });
  }

  public async fetchContentResponses({
    providerDocumentID,
    entryID,
    user,
    authRequestInfo,
    selectedPost,
    reload: reloadAll,
    referenceFetchBoundaries,
    product,
    selectedVariantUID,
    productCollection,
  }: ContentAPIRequest): Promise<ClientReviewRequestResult | null> {
    const fetchURL = await createAPIRequestURL(authRequestInfo, '/responses');

    if (!fetchURL) {
      console.error('Content: cannot create fetch URL');
      logAnalyticsEvent({
        category: 'exception',
        event: `Content: cannot create fetch URL`,
      });
      return Promise.resolve(null);
    }

    return this.fetchReviewResponses({
      providerDocumentID,
      entryID,
      product,
      selectedVariantUID,
      user,
      authRequestInfo,
      fetchURL,
      selectedPost,
      existingPostsToKeep: undefined,
      reloadAll,
      referenceFetchBoundaries,
      requiresProduct: false,
      productCollection,
    });
  }

  public async fetchReviewResponses({
    providerDocumentID,
    entryID,
    product,
    selectedVariantUID,
    user,
    authRequestInfo,
    fetchURL,
    selectedPost,
    existingPostsToKeep,
    reloadAll,
    referenceFetchBoundaries,
    requiresProduct,
    productCollection,
    category,
  }: ReviewResponseRequestProps): Promise<ClientReviewRequestResult | null> {
    if ((!product && requiresProduct) || !user || !fetchURL) {
      console.error(`fetchReviewResponses missing required entries`);
      logAnalyticsEvent({
        category: 'exception',
        event: `fetch-review-responses`,
        name: `Missing required entries`,
        dimensions: { provider: this.clientId, description: fetchURL.toString() } as ExceptionDimensions,
      });
      return null;
    }

    try {
      const userInfo = createReviewRequestUserInfo(user, { category: category ?? product?.matchingCategory, requiresCompleted: true });
      const clientDetails = await getClientDetails();

      const body: ReviewRequest & ContentReviewRequest = {
        provider: providerDocumentID,
        user: user.uid,
        userInfo,
        clientDetails,
        product: product?.documentID,
        selectedVariantUID,
      };

      if (this.isPreview) {
        body.isPreview = true;
      }

      if (entryID) {
        body.entryID = entryID;
      }
      if (selectedPost) {
        body.selectedPost = selectedPost;
      }
      if (existingPostsToKeep) {
        body.existingPostsToKeep = existingPostsToKeep;
      }
      if (reloadAll) {
        body.reloadAll = reloadAll;
      }
      if (referenceFetchBoundaries) {
        body.referenceFetchBoundaries = referenceFetchBoundaries;
      }
      if (productCollection) {
        body.productCollectionUID = productCollection.productCollectionUID;
      }

      if (this.isSDK) {
        body.isSDK = this.isSDK;
      }

      if (this.clientExperiments) {
        body.experiments = this.clientExperiments;
      }

      const result = await fetchRequest({
        url: fetchURL.toString(),
        method: 'POST',
        body,
        iOSBundleIdentifier: this.iOSBundleIdentifier,
        authRequestInfo,
      });

      if (result.status !== ResponseCode.success) {
        console.error(`Get review responses error ${result.status}`);
        logAnalyticsEvent({
          category: 'exception',
          event: `fetch-review-responses`,
          name: `${result.status}`,
          dimensions: { provider: this.clientId, description: await result.text() } as ExceptionDimensions,
        });
        return null;
      }

      const serverResponse = (await result.json()) as ServerReviewRequestResult;

      const clientResponse = createClientResponse(serverResponse);

      return clientResponse;
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: `review-req req`,
        name: error.message,
      });
      return null;
    }
  }

  public async fetchSizeRecommendations({
    product,
    selectedVariantUID,
    user,
    authRequestInfo,
  }: MatchAPIRequest): Promise<SizeRecommendationRequestResult | null> {
    const fetchURL = await createAPIRequestURL(authRequestInfo, '/size-recommendations');

    if (!fetchURL) {
      console.error('Carousel: cannot create fetch URL');
      logAnalyticsEvent({
        category: 'exception',
        event: `SizeRec: cannot create fetch URL`,
      });
      return Promise.resolve(null);
    }

    if (!product || !user || !fetchURL) {
      console.error(`sizeRec missing required entries`);
      logAnalyticsEvent({
        category: 'exception',
        event: `size-recommendation`,
        name: `Missing required entries`,
        dimensions: { provider: this.clientId, description: fetchURL.toString() } as ExceptionDimensions,
      });
      return null;
    }

    try {
      const userInfo = createReviewRequestUserInfo(user, { category: product.matchingCategory, requiresCompleted: true });
      const clientDetails = await getClientDetails();

      const body: SizeRecommendationRequest = {
        product: product.documentID,
        selectedVariantUID,
        provider: product.providerDocumentID,
        user: user.uid,
        userInfo,
        clientDetails,
      };

      if (this.isPreview) {
        body.isPreview = true;
      }

      if (this.isSDK) {
        body.isSDK = this.isSDK;
      }

      if (this.clientExperiments) {
        body.experiments = this.clientExperiments;
      }

      const result = await fetchRequest({
        url: fetchURL.toString(),
        method: 'POST',
        body,
        iOSBundleIdentifier: this.iOSBundleIdentifier,
        authRequestInfo,
      });

      if (result.status !== ResponseCode.success) {
        console.error(`Fetch size recommendation error ${result.status}`);
        logAnalyticsEvent({
          category: 'exception',
          event: `fetch-size-rec`,
          name: `${result.status}`,
          dimensions: { provider: this.clientId, description: await result.text() } as ExceptionDimensions,
        });
        return null;
      }

      const response = (await result.json()) as SizeRecommendationRequestResult;
      return response;
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: `size-req req`,
        name: error.message,
      });
      return null;
    }
  }

  public async translateReviewText(request: TranslateReviewRequest, authRequestInfo: AuthorizedRequestInfo): Promise<TranslateReviewResponse | null> {
    const fetchURL = await createAPIRequestURL(authRequestInfo, '/translate-post-review');

    if (!fetchURL) {
      return null;
    }

    try {
      const result = await fetchRequest({
        url: fetchURL.toString(),
        method: 'POST',
        body: request,
        iOSBundleIdentifier: this.iOSBundleIdentifier,
        authRequestInfo,
      });

      const statusCode = result.status;
      if (statusCode !== ResponseCode.success) {
        console.error(`Translate text request returned error ${result.status}`);
        const message = await result.text();
        logAnalyticsEvent({
          category: 'exception',
          event: `translate-review-text`,
          name: `${result.status}`,
          dimensions: { description: message } as ExceptionDimensions,
        });
        return null;
      }

      const responseAsText = (await result.text()) as string | null;
      if (!responseAsText || typeof responseAsText !== 'string') {
        return null;
      }
      return JSON.parse(responseAsText);
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: `translate-review-text-req`,
        name: error.message,
      });
      return null;
    }
  }

  public async recordSizeRecFineTune({
    product,
    selectedVariantUID,
    user,
    authRequestInfo,
  }: MatchAPIRequest): Promise<[success: boolean, error: Error | undefined]> {
    const fetchURL = await createAPIRequestURL(authRequestInfo, '/size-recommendations/tune');

    if (!fetchURL) {
      return [false, new Error('Auth missing')];
    }

    const userInfo = createReviewRequestUserInfo(user, { category: product.matchingCategory, requiresCompleted: true });
    const userUID = user.uid;

    if (!userUID) {
      return [false, new Error('Missing user uid')];
    }
    if (!selectedVariantUID) {
      return [false, new Error('Missing selected variant')];
    }
    if (!userInfo) {
      return [false, new Error('Missing user info')];
    }

    const request: SizeRecDataFineTuneRequest = {
      product: product.documentID,
      variant: selectedVariantUID,
      user: userUID,
      userInfo,
      provider: product.providerDocumentID,
    };

    try {
      const response = await fetchRequest({
        url: fetchURL.toString(),
        method: 'POST',
        body: request,
        iOSBundleIdentifier: this.iOSBundleIdentifier,
        authRequestInfo,
      });

      const statusCode = response.status;
      if (statusCode !== ResponseCode.success) {
        console.error(`Size rec fine tune request returned error ${response.status}`);
        const message = `${response.status}: ${response.statusText}`;
        logAnalyticsEvent({
          category: 'exception',
          event: `size-rec-tune`,
          name: `${response.status}`,
          dimensions: { description: message } as ExceptionDimensions,
        });
        return [false, new Error(message)];
      }

      return [true, undefined];
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: `size-rec-tune-rec`,
        name: error.message,
      });
      return [false, error];
    }
  }
}
